'use client';

import { DeepResearchSidebarHistoryList } from '@/components/sidebar-history/deep-research-sidebar-history-list';
import { SearchSidebarHistoryList } from '@/components/sidebar-history/search-sidebar-history-list';
import { TranslateSidebarHistoryList } from '@/components/sidebar-history/translate-sidebar-history-list';
import { SmartLink } from '@/components/smart-link';
import { ChevronDownIcon } from 'lucide-react';
import { useEffect, useState } from 'react';

const SIDEBAR_HISTORY_TYPE_STORAGE_KEY = 'suppr.sidebar.history-type';

export type SidebarHistoryType = 'search' | 'translate' | 'deep-research';

const SIDEBAR_HISTORY_TYPE_LABELS: Record<SidebarHistoryType, string> = {
  search: '文献检索',
  translate: '文档翻译',
  'deep-research': '深度研究',
};

interface SidebarHistoryPanelProps {
  pathname: string;
  currentSessionId: string | null;
  currentTranslationId: string | null;
  currentDeepResearchId: string | null;
  isAuthenticated: boolean;
}

export function SidebarHistoryPanel({
  pathname,
  currentSessionId,
  currentTranslationId,
  currentDeepResearchId,
  isAuthenticated,
}: SidebarHistoryPanelProps) {
  const [historyExpanded, setHistoryExpanded] = useState(true);
  const [historyType, setHistoryType] = useState<SidebarHistoryType>(() => {
    if (typeof window === 'undefined') {
      return 'search';
    }
    const savedType = window.localStorage.getItem(
      SIDEBAR_HISTORY_TYPE_STORAGE_KEY,
    );
    return savedType === 'translate' || savedType === 'deep-research'
      ? savedType
      : 'search';
  });

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    window.localStorage.setItem(SIDEBAR_HISTORY_TYPE_STORAGE_KEY, historyType);
  }, [historyType]);

  const viewAllHref =
    historyType === 'search'
      ? '/histories?type=search'
      : historyType === 'translate'
        ? '/histories?type=translate'
        : '/histories?type=deep-research';

  return (
    <div className="mt-3 min-h-0 flex-1 border-t border-sidebar-border pt-3">
      <div className="px-4">
        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            className="flex items-center gap-1 text-sm font-semibold text-foreground"
            onClick={() => setHistoryExpanded((prev) => !prev)}
          >
            <span>历史记录</span>
            <ChevronDownIcon
              className={`h-4 w-4 text-muted-foreground transition-transform ${
                historyExpanded ? '' : '-rotate-90'
              }`}
            />
          </button>
          <SmartLink
            href={viewAllHref}
            authenticated={isAuthenticated}
            mode="intent"
            className="text-xs text-muted-foreground transition-colors hover:text-foreground"
          >
            查看全部
          </SmartLink>
        </div>
      </div>
      {historyExpanded && (
        <>
          <div className="px-3">
            <div className="mt-2 flex mx-auto items-center w-full justify-around gap-1 rounded-full bg-neutral-200 p-1 dark:bg-neutral-800">
              {(
                Object.keys(SIDEBAR_HISTORY_TYPE_LABELS) as SidebarHistoryType[]
              ).map((type) => (
                <button
                  key={type}
                  type="button"
                  className={`rounded-full px-2 py-1 text-xs font-medium transition-colors ${
                    historyType === type
                      ? 'dark:bg-background text-foreground bg-neutral-100'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                  onClick={() => setHistoryType(type)}
                >
                  {SIDEBAR_HISTORY_TYPE_LABELS[type]}
                </button>
              ))}
            </div>
          </div>
          <div className="mt-2 max-h-[calc(100svh-26.5rem)] custom-scrollbar space-y-1 overflow-y-auto overscroll-contain pl-3 pr-1">
            {historyType === 'search' ? (
              <SearchSidebarHistoryList
                currentSessionId={currentSessionId}
                isSearchRoute={pathname.startsWith('/search')}
                isAuthenticated={isAuthenticated}
              />
            ) : historyType === 'translate' ? (
              <TranslateSidebarHistoryList
                currentTranslationId={currentTranslationId}
                isTranslateRoute={pathname.startsWith('/translate/preview')}
                isAuthenticated={isAuthenticated}
              />
            ) : (
              <DeepResearchSidebarHistoryList
                currentDeepResearchId={currentDeepResearchId}
                isDeepResearchRoute={pathname.startsWith('/deep-research/')}
                isAuthenticated={isAuthenticated}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
}
